import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Loader2, Plus, Folder, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import type { Id } from '../../convex/_generated/dataModel';
import type { IAvatarFolderWithCount } from '@/@types/avatars';

interface IAvatarFolderGridProps {
  folders: IAvatarFolderWithCount[] | undefined;
  isLoading: boolean;
  onEditFolder: (id: Id<'avatarFolders'>, title: string) => void;
  onDeleteFolder: (id: Id<'avatarFolders'>) => void;
}

export function AvatarFolderGrid({
  folders,
  isLoading,
  onEditFolder,
  onDeleteFolder,
}: IAvatarFolderGridProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!folders || folders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Folder className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-medium text-foreground">No avatars yet</h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-sm">
          Generate your first AI avatar image to build your library.
        </p>
        <Button
          onClick={() => navigate('/ai-avatar/my-avatars/generate')}
          className="mt-4"
          size="sm"
        >
          <Plus className="h-4 w-4 mr-2" />
          Generate First Avatar
        </Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {folders.map((folder: IAvatarFolderWithCount) => (
        <div
          key={folder._id}
          role="button"
          tabIndex={0}
          className="group relative bg-card rounded-lg border border-border overflow-hidden cursor-pointer hover:border-primary transition-colors"
          onClick={() => navigate(`/ai-avatar/my-avatars/folder/${folder._id}`)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`/ai-avatar/my-avatars/folder/${folder._id}`);
            }
          }}
        >
          <div className="aspect-[3/4] bg-muted relative">
            {folder.thumbnailUrl ? (
              <>
                <img
                  src={folder.thumbnailUrl}
                  alt={folder.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent" />
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Folder className="h-12 w-12 text-muted-foreground/50" />
              </div>
            )}

            {folder.thumbnailUrl && (
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <h3 className="font-medium text-white truncate">{folder.title}</h3>
                <p className="text-xs text-white/70">
                  {folder.imageCount} {folder.imageCount === 1 ? 'image' : 'images'}
                </p>
              </div>
            )}
          </div>

          {!folder.thumbnailUrl && (
            <div className="p-3">
              <h3 className="font-medium text-foreground truncate">{folder.title}</h3>
              <p className="text-xs text-muted-foreground">
                {folder.imageCount} {folder.imageCount === 1 ? 'image' : 'images'}
              </p>
            </div>
          )}

          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 bg-background/80 backdrop-blur-sm"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation();
                    onEditFolder(folder._id, folder.title);
                  }}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={e => {
                    e.stopPropagation();
                    onDeleteFolder(folder._id);
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      ))}
    </div>
  );
}
